import React, {useEffect, useRef, useState} from "react";
import {Dialog, IconButton} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import {loadGoogleMaps} from "../lib/googleMaps";
import "./VehicleLocationModal.css";

// How long ago vehiclePosition.cached_at (the shared TrackGPS cache row's
// last refresh, see trackgps.ListVehiclePositions on the backend) was —
// mirrors Map/index.js's formatCacheAge exactly, kept separate rather than
// shared since this repo doesn't have a common utils module and every other
// small formatter here (money, pad, formatDateTime...) is per-file too.
const formatCacheAge = (cachedAt, now) => {
  if (!cachedAt) {
    return null;
  }
  const seconds = Math.max(0, Math.round((now - new Date(cachedAt)) / 1000));
  if (seconds < 5) {
    return 'acum';
  }
  if (seconds < 60) {
    return `acum ${seconds} secunde`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `acum ${minutes} ${minutes === 1 ? 'minut' : 'minute'}`;
  }
  const hours = Math.round(minutes / 60);
  return `acum ${hours} ${hours === 1 ? 'oră' : 'ore'}`;
};

export default function VehicleLocationModal({vehiclePosition, delivererName, onClose}) {
  const mapContainerRef = useRef(null);
  const [now, setNow] = useState(() => new Date());

  // Re-created every time a vehiclePosition comes in — the Dialog unmounts
  // this whole subtree on close (see the early return below), so there's no
  // long-lived map instance to just re-center.
  useEffect(() => {
    if (!vehiclePosition) {
      return;
    }
    let cancelled = false;
    loadGoogleMaps().then((maps) => {
      if (cancelled || !mapContainerRef.current) {
        return;
      }
      const position = {lat: vehiclePosition.latitude, lng: vehiclePosition.longitude};
      const map = new maps.Map(mapContainerRef.current, {center: position, zoom: 15});
      new maps.Marker({position, map});
    }).catch(() => {
    });
    return () => {
      cancelled = true;
    };
  }, [vehiclePosition]);

  // Ticks the "cached X ago" line while the modal stays open — the position
  // itself is a one-off snapshot from when it was opened (Orders.js doesn't
  // keep pushing fresh vehiclePosition props in), but the age display should
  // still reflect real elapsed time, not freeze at whatever it read on open.
  useEffect(() => {
    if (!vehiclePosition) {
      return;
    }
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, [vehiclePosition]);

  if (!vehiclePosition) {
    return null;
  }

  const ignitionOn = vehiclePosition.ignition_state === 'ON';
  const cacheAge = formatCacheAge(vehiclePosition.cached_at, now);

  return (
    <Dialog open={!!vehiclePosition} onClose={onClose} fullWidth maxWidth="sm">
      <div className="vehicle-modal">
        <div className="vehicle-modal-topbar">
          <span className="vehicle-modal-title">{delivererName}</span>
          <IconButton className="vehicle-modal-close" size="small" onClick={onClose}>
            <CloseIcon fontSize="small"/>
          </IconButton>
        </div>

        <div className="vehicle-modal-map" ref={mapContainerRef}/>

        <div className="vehicle-modal-info">
          <div className="vehicle-modal-info-row">
            <span className="vehicle-modal-info-label">Mașină</span>
            <span>{vehiclePosition.make} {vehiclePosition.model} — {vehiclePosition.plate_number}</span>
          </div>
          <div className="vehicle-modal-info-row">
            <span className="vehicle-modal-info-label">Stare motor</span>
            <span className={`vehicle-modal-ignition-badge ${ignitionOn ? 'on' : 'off'}`}>
              {ignitionOn ? 'Pornită' : 'Oprită'}
            </span>
          </div>
          <div className="vehicle-modal-info-row">
            <span className="vehicle-modal-info-label">Viteză</span>
            <span>{Math.round(vehiclePosition.speed || 0)} km/h</span>
          </div>
          {cacheAge && (
            <div className="vehicle-modal-info-row">
              <span className="vehicle-modal-info-label">Actualizat</span>
              <span>{cacheAge}</span>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
